import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import CardContentMUI from "@material-ui/core/CardContent";

// Config
import { getRoute, urls } from "../../config/routes";
import { snackbarTypes } from "../../config/snackbar";
import colors from "../../config/colors";
import styles from "../../config/styles";
import isCaseLocked from "../../utils/isCaseLocked";

// Actions
import { deleteCase } from "../../state/caseDetail/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Icons
import lockIcon from "../../assets/icons/restrictedIcon.svg";
import reportIcon from "../../assets/icons/reportIcon.svg";
import submitIcon from "../../assets/icons/submit_ico.svg";
import trashIcon from "../../assets/icons/trash.svg";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useShowSteps from "../../hooks/useShowSteps";
import useTranslate from "../../hooks/useTranslate";

// Components
import CardHeader from "../CardHeader";
import ConfirmationDialog from "../ConfirmationDialog";
import PinPasscodeDialog from "../PinPasscodeDialog";
import Text from "../Text";
import { userTypes } from "src/config/values";

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: styles.values.border,
    padding: theme.spacing(3.5, 4, 3),
    position: "relative"
  },
  headerLocked: {
    backgroundColor: colors.lockedBackground
  },
  headerIntel: {
    backgroundColor: "#E8E7FC"
  },
  headerRequiredAction: {
    backgroundColor: colors.hintOfRed
  },
  title: {
    color: "inherit",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  titleLocked: {
    color: colors.lockedCardText,
    wordBreak: "break-word"
  },
  lockedText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: colors.lockedCardText,
    marginTop: theme.spacing(1)
  },
  lockIcon: {
    position: "absolute",
    top: theme.spacing(3.5),
    right: theme.spacing(4),
    width: "20px",
    height: "20px"
  }
}));

const CardContent = ({ data, isCollab }) => {
  const [showDelete, setShowDelete] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const { isDeleting } = useSelector(s => s.caseDetail);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const getText = useTranslate();
  const history = useHistory();

  const caseId = data.id;
  const showSteps = useShowSteps(caseId);

  const title = data.number;

  const menuOptions = useMemo(() => {
    const result = [];

    if (data.isDraft && !isCollab && !showSteps) {
      result.push({
        key: 1,
        text: "common.prompts.shareWith",
        icon: submitIcon,
        onClick: () => history.push(getRoute(urls.shareWith, { caseId }))
      });
    }

    if (!isCaseLocked(data) && !isCollab) {
      result.push({
        key: 2,
        text: "casesPage.caseCard.menu.delete",
        icon: trashIcon,
        onClick: () => setShowDelete(true)
      });
    }

    if (data.userCapacity === "PoliceOfficer") {
      result.push({
        key: 4,
        text: "casesPage.caseCard.menu.caseLog",
        icon: reportIcon,
        onClick: () => history.push(getRoute(urls.caseLog, { caseId }))
      });
    }

    return result;
  }, [caseId, data.isDraft, data.isLocked, history, isCollab, showSteps]);

  const isDeletingCase = isDeleting === caseId;

  const handleClose = () => {
    setShowDelete(false);
    setShowPasscode(false);
  };

  const handleOpenPasscode = () => {
    setShowDelete(false);
    setShowPasscode(true);
  };

  const handleDelete = () => {
    setShowPasscode(false);
    setShowDelete(true);
    dispatch(
      deleteCase({
        caseId,
        onFail: () => {
          trackException(`Failed to delete case: ${caseId}`);
          handleClose();
          // Not refetching the list here - REQUEST_CASES always clears
          // cases.data while it's in flight (no "same data, just refresh"
          // mode like getCaseDetail's isUpdate), so it would blank out the
          // whole list for every card just to recover from one failed
          // delete.
          dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
        }
      })
    );
  };

  return (
    <>
      <CardContentMUI
        className={clsx(classes.header, {
          [classes.headerLocked]: data.isRestrictedForCurrentUser,
          [classes.headerIntel]: data.isIntel && !data.isDraft,
          [classes.headerRequiredAction]: data.hasPendingStatements
        })}
      >
        <CardHeader
          actionRequired={data.hasPendingStatements}
          isRestricted={data.isRestrictedForCurrentUser}
          to={getRoute(urls.caseDetail, { caseId })}
          caseNumber={data.number}
          title={
            data.isRestrictedForCurrentUser ? (
              <span className={classes.titleLocked}>{title}</span>
            ) : (
              <Link
                className={classes.title}
                to={getRoute(urls.caseDetail, { caseId })}
              >
                {title}
              </Link>
            )
          }
          options={menuOptions}
          showOptions
          isIntel={data.isIntel}
          intelIconLeft
          showIntelIcon={false}
          compactTitle
        />
        {data.isRestrictedForCurrentUser && (
          <>
            <img src={lockIcon} alt="" className={classes.lockIcon} />
            <Text
              className={classes.lockedText}
              text="casesPage.caseCard.lockedCase.label"
              type="body"
            />
          </>
        )}
      </CardContentMUI>
      <ConfirmationDialog
        onClose={handleClose}
        onConfirm={handleOpenPasscode}
        open={showDelete}
        texts={[
          { key: 1, text: getText("deleteCaseDialog.title") },
          { key: 2, text: getText("deleteCaseDialog.description") }
        ]}
        type="delete"
        button={getText("common.prompts.delete").toUpperCase()}
        cancel={getText("common.prompts.cancel").toUpperCase()}
        isLoading={isDeletingCase}
      />
      <PinPasscodeDialog
        onClose={handleClose}
        onSubmit={handleDelete}
        open={showPasscode}
      />
    </>
  );
};

CardContent.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    isDraft: PropTypes.bool,
    hasPendingStatements: PropTypes.bool,
    isIntel: PropTypes.bool,
    isLocked: PropTypes.bool,
    isRestrictedForCurrentUser: PropTypes.bool,
    isOwnedByUser: PropTypes.bool,
    number: PropTypes.string,
    userCapacity: PropTypes.string
  }).isRequired,
  isCollab: PropTypes.bool
};

CardContent.defaultProps = {
  isCollab: false
};

export default CardContent;
